// BPI-CPI-MODULE: Barber Performance Indicator Service
// Tracks staff performance and career progression for SLIK POS

export interface BPIMetrics {
  totalAppointments: number;    // Total completed appointments
  totalRevenue: number;         // Total revenue generated
  totalTips: number;            // Total tips earned
  rebookRate: number;           // % of clients who rebook
  averageRating: number;        // Average customer rating (1-5)
  punctualityScore: number;     // On-time percentage
  employmentStartDate: Date;    // Start date of employment
  specialtyCertifications: string[]; // Hair techniques certified in
  teamworkScore: number;        // Peer rating (1-5)
  upsellRate: number;           // % appointments with add-ons
}

export interface BPIResult {
  score: number;                // 0-100 BPI score
  rank: 'New Talent' | 'Emerging' | 'Pro' | 'Master' | 'Legend';
  title: string;                // Display title
  level: number;                // Numeric level (1-5)
  badges: string[];             // Earned badges
  perks: string[];              // Unlocked perks
  nextRankRequirements?: string; // Requirements for next rank
}

export class BPIService {

  /**
   * Calculate BPI score for a barber based on their metrics
   */
  static calculateBPI(barberId: string, metrics: BPIMetrics): BPIResult {
    let score = 0;

    // 1. Experience Volume (25 points max)
    const volumeScore = Math.min(25, Math.max(0, ((metrics.totalAppointments - 10) / 190) * 25));
    score += volumeScore;

    // 2. Revenue Generation (20 points max)
    const revenueScore = Math.min(20, Math.max(0, ((metrics.totalRevenue - 500) / 4500) * 20));
    score += revenueScore;

    // 3. Client Satisfaction (20 points max)
    const satisfactionScore = Math.min(20, Math.max(0, ((metrics.averageRating - 3.0) / 1.5) * 20));
    score += satisfactionScore;

    // 4. Client Retention (15 points max)
    const retentionScore = Math.min(15, Math.max(0, ((metrics.rebookRate - 30) / 40) * 15));
    score += retentionScore;

    // 5. Professional Skills (10 points max)
    const skillsScore = Math.min(10, metrics.specialtyCertifications.length * 2);
    score += skillsScore;

    // 6. Punctuality (5 points max)
    const punctualityScore = Math.min(5, Math.max(0, ((metrics.punctualityScore - 80) / 20) * 5));
    score += punctualityScore;

    // 7. Teamwork (5 points max)
    const teamworkScore = Math.min(5, Math.max(0, ((metrics.teamworkScore - 3.0) / 2.0) * 5));
    score += teamworkScore;

    // Ensure score is between 0-100
    score = Math.max(0, Math.min(100, Math.round(score)));

    // Determine rank and benefits
    const result = this.determineRank(score, metrics);

    console.log(`📊 BPI Calculated for barber ${barberId}:`, {
      score,
      rank: result.rank,
      breakdown: {
        volume: Math.round(volumeScore),
        revenue: Math.round(revenueScore),
        satisfaction: Math.round(satisfactionScore),
        retention: Math.round(retentionScore),
        skills: Math.round(skillsScore),
        punctuality: Math.round(punctualityScore),
        teamwork: Math.round(teamworkScore)
      }
    });

    return result;
  }

  /**
   * Determine rank based on BPI score and additional criteria
   */
  private static determineRank(score: number, metrics: BPIMetrics): BPIResult {
    const monthsEmployed = this.getMonthsDifference(metrics.employmentStartDate, new Date());

    if (score >= 90 && metrics.totalAppointments >= 500 && monthsEmployed >= 24) {
      return {
        score,
        rank: 'Legend',
        title: 'Master Barber Legend',
        level: 5,
        badges: ['🏆', '👑', '⭐', '💎', '🎯'],
        perks: [
          'Equity participation opportunity',
          'Training academy instructor',
          'Premium booking premium (30%)',
          'Flexible schedule control',
          'Client base ownership rights'
        ],
        nextRankRequirements: 'Maximum rank achieved!'
      };
    }

    if (score >= 75 && metrics.totalAppointments >= 200 && monthsEmployed >= 12) {
      return {
        score,
        rank: 'Master',
        title: 'Master Barber',
        level: 4,
        badges: ['👑', '⭐', '💎', '🎯'],
        perks: [
          'Premium booking premium (20%)',
          'Mentor junior barbers',
          'Advanced scheduling priority',
          'Performance bonuses',
          'Professional development budget'
        ],
        nextRankRequirements: `Reach 90+ BPI score, 500+ appointments, 24+ months for Legend`
      };
    }

    if (score >= 60 && metrics.totalAppointments >= 75 && monthsEmployed >= 6) {
      return {
        score,
        rank: 'Pro',
        title: 'Professional Barber',
        level: 3,
        badges: ['⭐', '💎', '🎯'],
        perks: [
          'Premium booking premium (10%)',
          'Advanced service offerings',
          'Client retention bonuses',
          'Flexible break scheduling'
        ],
        nextRankRequirements: `Reach 75+ BPI score, 200+ appointments, 12+ months for Master`
      };
    }

    if (score >= 40 && metrics.totalAppointments >= 25 && monthsEmployed >= 2) {
      return {
        score,
        rank: 'Emerging',
        title: 'Emerging Barber',
        level: 2,
        badges: ['💎', '🎯'],
        perks: [
          'Basic commission structure',
          'Training program access',
          'Standard booking priority'
        ],
        nextRankRequirements: `Reach 60+ BPI score, 75+ appointments, 6+ months for Pro`
      };
    }

    return {
      score,
      rank: 'New Talent',
      title: 'New Talent',
      level: 1,
      badges: ['🎯'],
      perks: [
        'Mentorship program',
        'Basic training certification',
        'Standard hourly wage'
      ],
      nextRankRequirements: `Reach 40+ BPI score, 25+ appointments, 2+ months for Emerging`
    };
  }

  /**
   * Get sample BPI data for testing/demo purposes
   */
  static getSampleBPIData(): Record<string, BPIResult> {
    return {
      'james': {
        score: 88,
        rank: 'Master',
        title: 'Master Barber',
        level: 4,
        badges: ['👑', '⭐', '💎', '🎯'],
        perks: ['Premium booking premium (20%)', 'Mentor juniors', 'Performance bonuses'],
        nextRankRequirements: 'Reach 90+ BPI for Legend status'
      },
      'kelly': {
        score: 72,
        rank: 'Pro',
        title: 'Professional Barber',
        level: 3,
        badges: ['⭐', '💎', '🎯'],
        perks: ['Premium booking premium (10%)', 'Advanced services', 'Retention bonuses'],
        nextRankRequirements: 'Reach 75+ BPI for Master status'
      },
      'jose': {
        score: 85,
        rank: 'Master',
        title: 'Master Barber',
        level: 4,
        badges: ['👑', '⭐', '💎', '🎯'],
        perks: ['Premium booking premium (20%)', 'Mentor juniors', 'Performance bonuses'],
        nextRankRequirements: 'Reach 90+ BPI for Legend status'
      }
    };
  }

  /**
   * Get commission rate based on BPI rank
   */
  static getCommissionRate(rank: string): number {
    const rates: Record<string, number> = {
      'Legend': 70,
      'Master': 60,
      'Pro': 50,
      'Emerging': 40,
      'New Talent': 30
    };
    return rates[rank] || 30;
  }

  // Helper function
  private static getMonthsDifference(start: Date, end: Date): number {
    return Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24 * 30));
  }
}

// Export types for use in other components
export type BPIRank = BPIResult['rank'];
export type BPILevel = BPIResult['level'];
